<script setup lang="ts">
import type { ContentItem } from "@edco/apollo";
import LayoutDefault from "./default.vue";
import type { AdminNavItem } from "~/components/admin/AdminNav.vue";

const pageData = useHeadlessDataState();

const contentResource = useContentDeliveryResource();
const dollarMemberResource = $isnMemberResource();

const authStore = useAuthStore();

if ( !authStore.isLoggedIn ) {
  throw createError( "Member not found" );
}

const content = await contentResource.getByTypeSingle( "myAccount" );
const children = await contentResource.getChildren( content.data.value.id, {
  sort: ["sortOrder:asc"],
} );

// Hiding some nav options
// const navItems = computed<AdminNavItem[]>( () => children.data.value?.items
//   .map( ( item: ContentItem ) => ( {
//     label: item.properties.adminNavLabel || item.name,
//     icon: item.properties.adminNavIcon,
//     url: item.route.path,
//   } ) ) || [] );

const navItems = computed<AdminNavItem[]>( () => {
  const items = children.data.value?.items || [];

  // Filter out the specific items you don't want
  const filteredItems = items.filter( ( item: ContentItem ) => {
    const urlsToExclude = ["/my-account/networks", "/my-account/contributions"];
    return !urlsToExclude.includes( item.route.path );
  } );

  return filteredItems.map( ( item: ContentItem ) => ( {
    label: item.properties.adminNavLabel || item.name,
    icon: item.properties.adminNavIcon,
    url: item.route.path,
  } ) );
} );

const route = useRoute();
if ( pageData.value.data.route.path === content.data.value.route.path ) {
  await navigateTo( navItems.value[0].url );
}

async function removeDisplayPicture () {
  await dollarMemberResource.removeDisplayPicture();
}

async function uploadDisplayPicture ( formData: FormData ) {
  return dollarMemberResource.uploadDisplayPicture( formData );
}
</script>

<template>
  <LayoutDefault>
    <div
      v-if="authStore"
      class="member-section">
      <div class="container">
        <div class="member-section__content">
          <admin-nav
            :items="navItems"
            :name="authStore.webAccount.firstName"
            :profile-image="authStore.webAccount.displayPicture"
            :upload-callback="uploadDisplayPicture"
            :remove-callback="removeDisplayPicture"
            :active-route="route.path?.replace('my-account-index-', '')" />

          <div class="member-section__child">
            <div class="member-section__header">
              <h1 class="h2 member-section__heading">
                Welcome back, {{ authStore.webAccount.firstName }}
              </h1>

              <a-button
                to="/logout"
                variant="secondary"
                class="member-section__button">
                Log out
              </a-button>
            </div>

            <slot />
          </div>
        </div>
      </div>
    </div>
  </LayoutDefault>
</template>

<style scoped lang="scss">
.member-section {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 0;

    @media ( max-width: 600px ) {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  &__heading {
    margin: 0;
    min-height: 70px;
    font-weight: 300;
    font-size: 2rem;
    padding-right: 10px;

    @media ( max-width: 600px ) {
      padding-right: 0;
      margin-bottom: 10px;
      min-height: auto;
    }
  }

  &__button {
    white-space: nowrap;
  }

  &__content {
    display: flex;
    min-height: 500px;

    >:not( .admin-nav ) {
      flex-grow: 1;
    }

    @media ( max-width:  600px ) {
      flex-direction: column;
    }
  }
}
</style>
